import { NavLink, useLocation, useNavigate, type NavLinkProps } from 'react-router-dom';
import type { ReactNode } from 'react';
import { S } from '../i18n/strings';
import { useSession } from '../state/SessionContext';
import { BackIcon, CalendarIcon, HomeIcon, SearchIcon, UserIcon } from './components/icons';

/** Rutas raíz: las únicas con barra de navegación. */
const ROOT_ROUTES = ['/', '/buscar', '/reservas'];

/**
 * Marco de la aplicación.
 *
 * La barra de navegación solo existe en los destinos raíz: dentro del flujo de reserva
 * distraería y permitiría abandonar el checkout por accidente. En las pantallas de detalle se
 * cambia por un botón de volver.
 */
export function AppShell({ children }: { children: ReactNode }) {
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useSession();
  const isRoot = ROOT_ROUTES.includes(location.pathname);
  // La puerta es pantalla de personal: ni barra de comensal ni acceso al perfil.
  const isStaff = location.pathname.startsWith('/puerta');

  return (
    <div className="app-shell">
      <header className="app-header">
        {!isRoot && !isStaff && (
          <button type="button" className="btn--icon" onClick={() => navigate(-1)} aria-label={S.back}>
            <BackIcon />
          </button>
        )}
        <span className="app-header__title app-header__brand">{S.appName}</span>
        {!isStaff && (
          <button
            type="button"
            className="btn--icon"
            onClick={() => navigate('/perfil')}
            aria-label={S.cdProfile}
            title={user?.fullName || S.navProfile}
          >
            <UserIcon />
          </button>
        )}
      </header>

      {isRoot && !isStaff && (
        <nav className="bottom-nav" aria-label={S.appName}>
          <NavItem to="/" icon={<HomeIcon className="bottom-nav__icon" />} label={S.navHome} end />
          <NavItem to="/buscar" icon={<SearchIcon className="bottom-nav__icon" />} label={S.navSearch} />
          <NavItem
            to="/reservas"
            icon={<CalendarIcon className="bottom-nav__icon" />}
            label={S.navReservations}
          />
        </nav>
      )}

      <main className={`app-main ${isRoot ? 'app-main--with-nav' : ''}`}>{children}</main>
    </div>
  );
}

function NavItem({
  to,
  icon,
  label,
  end,
}: {
  to: NavLinkProps['to'];
  icon: ReactNode;
  label: string;
  end?: boolean;
}) {
  return (
    <NavLink to={to} end={end} className="bottom-nav__item">
      {icon}
      <span>{label}</span>
    </NavLink>
  );
}
